import React, { useEffect, useMemo, useState } from "react";
import { Building2, ChevronRight, Coins, Cpu, Wrench } from "lucide-react";

import { Card } from "@/components/ui/Card";
import DataTable from "@/components/shared/DataTable";
import StatusBadge from "@/components/shared/StatusBadge";
import { useDebounce } from "@/hooks/useDebounce";
import { CHART } from "@/pages/admin/compliance/analytics/components/primitives";
import UsageRankedBars from "@/components/usage/UsageRankedBars";
import UsageSearchField from "@/components/usage/UsageSearchField";
import UsageSectionHeader from "@/components/usage/UsageSectionHeader";
import UsageStat from "@/components/usage/UsageStat";
import { fmt, fmtCompact, fmtPct, pctOf, tenantName } from "@/components/usage/format";

/**
 * The fleet answer: how much in total, and who is heavy.
 *
 * Three layers, in the order the questions get asked:
 *
 * 1. the headline totals,
 * 2. one ranked chart — the heaviest tenants, which is the only thing a chart
 *    tells you here that the table does not,
 * 3. the full table, sortable, and every row a way into that tenant.
 *
 * The chart is the page's single fleet visual on purpose. A donut of the same
 * numbers, a trend line the API cannot serve and a second bar chart of calls
 * would each repeat this one without answering anything new.
 */

const CHART_LIMIT = 10;
const PAGE_SIZE = 25;

const FleetOverview = ({ tenants = [], totals, totalUses, callDetail, onSelectTenant }) => {
  const [search, setSearch] = useState("");
  // The input stays instant; the re-sort and re-render of every row waits.
  // Debounced even though the filter is local — this table re-lays out a row
  // per tenant, and doing that on each keystroke is what makes typing feel
  // heavy on a large fleet.
  const debouncedSearch = useDebounce(search, 250);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const fleetTokens = Number(totals?.totalTokens ?? 0);
  const showCalls = tenants.some((t) => t.llmCalls !== undefined && t.llmCalls !== null);

  // A narrowed filter can leave the reader on a page that no longer exists.
  useEffect(() => setPage(1), [debouncedSearch]);

  // Only the filter is done here. Sorting and paging are the shared table's,
  // which is where the sort state and the header controls live too.
  const rows = useMemo(() => {
    const keyword = debouncedSearch.trim().toLowerCase();
    if (!keyword) return tenants;
    return tenants.filter((t) => `${t.name ?? ""} ${t.slug ?? ""}`.toLowerCase().includes(keyword));
  }, [tenants, debouncedSearch]);

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Tenant",
        sortable: true,
        render: (_value, t) => (
          <>
            {/* The slug is a second line, not text run onto the end of the
                name — and it is dropped when it repeats the name, which is
                what turned "acme" into "acmeacme". */}
            <span className="flex items-center gap-1.5">
              <span className="min-w-0 truncate text-text-primary">{tenantName(t.name, t.slug, t.tenantId)}</span>
              {/* This table is the page's default view, so it is where a
                  deleted tenant is seen first. A tenant can be gone and still
                  own the tokens it burned, so the row stays — it just has to
                  say what it is. Same rule as the picker and the tenant
                  header: mark only the rows that need explaining, and let
                  Deleted win over Inactive. */}
              {(t.isDeleted || t.isActive === false) && (
                <span className="flex-shrink-0">
                  <StatusBadge size="sm" isActive={t.isActive} isDeleted={t.isDeleted} />
                </span>
              )}
            </span>
            {t.slug && t.name && t.slug !== t.name && <span className="block text-xs text-text-secondary">{t.slug}</span>}
            <span className="mt-1.5 block h-1 w-full max-w-[180px] overflow-hidden rounded-full bg-background-tertiary">
              <span className="block h-full rounded-full bg-tone-blue" style={{ width: `${Math.max(2, pctOf(t.totalTokens, fleetTokens))}%` }} />
            </span>
          </>
        ),
      },
      { key: "uses", label: "Tool uses", align: "right", sortable: true, className: "tabular-nums", render: (value) => fmt(value) },
      ...(showCalls
        ? [
            { key: "llmCalls", label: "LLM calls", align: "right", sortable: true, className: "tabular-nums", render: (value) => fmt(value) },
            { key: "cachedCalls", label: "Cached", align: "right", sortable: true, className: "tabular-nums", render: (value) => fmt(value) },
          ]
        : []),
      { key: "inputTokens", label: "Input", align: "right", sortable: true, className: "tabular-nums", render: (value) => fmt(value) },
      { key: "outputTokens", label: "Output", align: "right", sortable: true, className: "tabular-nums", render: (value) => fmt(value) },
      {
        key: "totalTokens",
        label: "Total tokens",
        align: "right",
        sortable: true,
        className: "tabular-nums",
        render: (value) => (
          <span title={fmt(value)}>
            {fmtCompact(value)}
            <span className="ml-2 text-[11px] text-text-secondary">{fmtPct(pctOf(value, fleetTokens))}</span>
          </span>
        ),
      },
      { key: "chevron", label: "", align: "right", render: () => <ChevronRight className="ml-auto h-4 w-4 text-text-secondary" /> },
    ],
    [showCalls, fleetTokens],
  );

  const chartData = useMemo(
    () =>
      [...tenants]
        .sort((a, b) => Number(b.totalTokens ?? 0) - Number(a.totalTokens ?? 0))
        .slice(0, CHART_LIMIT)
        .map((t) => ({ id: t.tenantId, name: tenantName(t.name, t.slug, t.tenantId), sublabel: t.slug, value: Number(t.totalTokens ?? 0) }))
        .filter((row) => row.value > 0),
    [tenants],
  );

  const busiest = chartData[0] ?? null;

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <UsageStat label="Tool uses" value={totalUses} accent={CHART.green} icon={Wrench} />
        <UsageStat label="Total tokens" value={fleetTokens} accent={CHART.indigo} icon={Coins} />
        <UsageStat
          label="Tenants with activity"
          value={tenants.filter((t) => Number(t.uses ?? 0) > 0 || Number(t.totalTokens ?? 0) > 0).length}
          accent={CHART.blue}
          icon={Building2}
        />
        {callDetail ? (
          <UsageStat
            label="LLM calls"
            value={callDetail.calls}
            accent={CHART.violet}
            icon={Cpu}
            hint={`${fmt(callDetail.cachedCalls)} more served from cache · ${fmt(callDetail.failedCalls)} failed`}
          />
        ) : (
          <UsageStat
            label="Busiest tenant"
            value={busiest?.value ?? 0}
            accent={CHART.amber}
            icon={Building2}
            hint={busiest ? `${busiest.name} — ${fmtPct(pctOf(busiest.value, fleetTokens))} of fleet tokens` : "No activity in this window"}
          />
        )}
      </div>

      <Card className="p-5">
        <UsageSectionHeader
          title="Where the tokens go"
          subtitle={
            chartData.length > 0
              ? `Heaviest ${chartData.length} of ${tenants.length} tenants. Select a bar to open that tenant. The full list is below.`
              : "No tenant consumed tokens in this window."
          }
        />
        <UsageRankedBars data={chartData} valueLabel="Total tokens" total={fleetTokens} onSelect={onSelectTenant} emptyNote="No tokens recorded in this period." />
      </Card>

      <Card className="p-5">
        <UsageSectionHeader
          title="Every tenant"
          subtitle="Select a row for that tenant's tools, features and people."
          aside={<UsageSearchField value={search} onChange={setSearch} placeholder="Filter tenants" clearLabel="Clear filter" inputClassName="min-w-[220px]" />}
        />

        {/* The table is dropped when there is nothing in it, rather than left to
            print its own generic "No data available" — which does not tell a
            SuperAdmin whether the fleet was quiet or the filter was too narrow. */}
        {rows.length > 0 ? (
          <DataTable
            columns={columns}
            rowKey="tenantId"
            apiData={rows}
            defaultSort={{ key: "totalTokens", order: "desc" }}
            onRowClick={(t) => onSelectTenant(t.tenantId)}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            paginationLabel="tenants"
          />
        ) : (
          <p className="py-10 text-center text-sm text-text-secondary">
            {debouncedSearch ? `No tenant matches "${debouncedSearch}".` : "No usage recorded in this period."}
          </p>
        )}
      </Card>
    </>
  );
};

export default FleetOverview;
